import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { generateFilterOptions } from 'utils';
import { Autocomplete } from 'components/FormFields';
import { FETCH_STATE } from 'constants';
import { AccountStatementActions } from 'state/modules/accountStatement';

const AccountStatementSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  style,
  form: { errors, touched, setFieldValue },
  disabled,
  id,
  'data-cy': datacy,
  customNoOptionText,
  isOwner,
}) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const filterOptions = generateFilterOptions('start', 'label');

  const options = useSelector((state) => state.accountStatement.list);
  const fetchingAccountStatement = useSelector((state) => state.accountStatement.fetchingList);
  const loading = fetchingAccountStatement === FETCH_STATE.LOADING;

  const fetchAccountStatement = useCallback(
    (params = {}) => {
      dispatch(AccountStatementActions.fetchAccountStatements(params));
    },
    [dispatch]
  );

  // Run once on mount, no debounce
  useEffect(() => {
    fetchAccountStatement({ ...params });
  }, []);

  const getOptionsLabel = (statement) => {
    if (!statement.id) return '';
    const statementAvailable = true;

    const invoiceDate = statementAvailable ? new Date(statement?.due_time) : null;
    const year =
      invoiceDate instanceof Date && !isNaN(invoiceDate)
        ? new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(invoiceDate)
        : '-';
    const month =
      invoiceDate instanceof Date && !isNaN(invoiceDate)
        ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(invoiceDate)
        : '-';
    const orgName = statement?.payer?.customer_name;
    const title = statementAvailable ? `${isOwner ? `${orgName}, ` : ''}${month}, ${year}` : '--';
    return title;
  };

  const getOptionsSelected = (option, value) => option?.id === value?.id;
  const handleOnChange = (ev, val) => setFieldValue(name, val?.id, val || '');

  const innerValue = useMemo(
    () => options.find((statement) => statement?.id == value) || '',
    [options, value]
  );

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      margin="none"
      dataCy={datacy}
      fullwidth="true"
      variant={variant}
      loading={loading}
      options={options}
      clearOnBlur={false}
      id={id}
      disabled={disabled}
      customNoOptionText={customNoOptionText}
      value={innerValue}
      filterOptions={filterOptions}
      getOptionLabel={getOptionsLabel}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={getOptionsSelected}
      onChange={handleOnChange}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

AccountStatementSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  style: {},
  customParams: {},
  label: 'Select...',
  variant: 'standard',
  datacy: 'vehicle-select',
  noOptionsText: undefined,
};

export { AccountStatementSelect };
